/** Reusable settings section shell — animated wrapper, section title, and raised card. */
import { motion } from 'motion/react';
import { ChevronRight } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Section shell ──────────────────────────────────────────────────────────

interface SettingsSectionProps {
  title: string;
  /** Animation stagger delay (seconds). */
  delay?: number;
  /** Visual variant — 'danger' uses error colour scheme. */
  variant?: 'default' | 'danger';
  /** Extra classes on the card container (e.g. "space-y-6"). */
  cardClassName?: string;
  children: React.ReactNode;
}

export function SettingsSection({
  title,
  delay = 0,
  variant = 'default',
  cardClassName,
  children,
}: SettingsSectionProps) {
  const isDanger = variant === 'danger';

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
    >
      <div
        className="rounded-[24px] p-5 learner-double:p-7"
        style={
          isDanger
            ? {
                background: 'var(--color-error-subtle)',
                border: '1px solid color-mix(in srgb, var(--color-error) 26%, transparent)',
                boxShadow: 'var(--shadow-sm)',
              }
            : {
                background: 'var(--color-surface-solid)',
                border: '1px solid var(--color-surface-border)',
                boxShadow: 'var(--shadow-sm)',
              }
        }
      >
        <h2
          className="text-section-title mb-6 learner-double:mb-7"
          style={{
            color: isDanger ? 'var(--color-error-text)' : 'var(--color-text)',
          }}
        >
          {title}
        </h2>

        <div className={cardClassName ?? 'space-y-6'}>{children}</div>
      </div>
    </motion.section>
  );
}

// ─── Collapsible row ────────────────────────────────────────────────────────

interface CollapsibleRowProps {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/** A labelled row with a ChevronRight toggle that expands to reveal content. */
export function CollapsibleRow({ label, open, onToggle, children }: CollapsibleRowProps) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="group flex w-full cursor-pointer items-center justify-between py-1.5"
      >
        <span
          className="text-list-title"
          style={{
            color: 'var(--color-text)',
          }}
        >
          {label}
        </span>
        <ChevronRight
          size={15}
          strokeWidth={2}
          className="transition-transform duration-200"
          style={{
            color: 'var(--color-text-muted)',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.2, ease: EASE }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </div>
  );
}
